import PropTypes from 'prop-types'
import { Container } from '../styled-components'
import { SearchContext } from './search-context'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { searchVariantsOptions } from '../constants'
import { isEmpty } from 'lodash'

const SearchRoot = forwardRef(
  (
    {
      variant = searchVariantsOptions.DESKTOP,
      disabled = false,
      customOnChange,
      setSearchValue = null,
      searchValue,
      defaultDesktopVariantOpen = false,
      children,
      startFocued = false,
      ...props
    },
    forwardedRef
  ) => {
    const [desktopVariantOpen, setDesktopVariantOpen] = useState(
      defaultDesktopVariantOpen
    )
    const [internalSearchValue, setInternalSearchValue] = useState('')

    const defineValue = searchValue ?? internalSearchValue
    const defineSetValue = setSearchValue ?? setInternalSearchValue

    const [showCancelButton, setShowCancelButton] = useState(false)
    const [showClearButton, setShowClearButton] = useState(
      !isEmpty(defineValue)
    )
    const [buttonSize, setButtonSize] = useState(null)

    const isOpenInput =
      desktopVariantOpen || variant !== searchVariantsOptions.DESKTOP

    const containerRef = useRef(null)
    const componentRef = useRef(null)
    const inputRef = forwardedRef ?? componentRef
    const cancelButtonRef = useRef(null)

    const handleChangeValue = (value) => {
      defineSetValue(value)

      if (customOnChange) {
        customOnChange(value)
      }
    }

    const handleClear = (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (inputRef && inputRef.current) {
        defineSetValue('')
        setShowClearButton(false)
        inputRef.current.focus()

        handleChangeValue('')
      }
    }

    const handleCancel = (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (inputRef && inputRef.current) {
        defineSetValue('')
        setButtonSize(0)
        setShowCancelButton(false)
        setShowClearButton(false)

        handleChangeValue('')
      }
    }

    const handleInputChange = (e) => {
      e.stopPropagation()
      const value = e.target.value
      handleChangeValue(value)
    }

    const handleFocus = (e) => {
      e.stopPropagation()
      const inputElement = cancelButtonRef.current

      if (inputElement) {
        setButtonSize(inputElement.offsetWidth + 8)
        setShowCancelButton(true)
      } else {
        setShowCancelButton(true)
      }
    }

    const handleOnBlur = (e) => {
      e.stopPropagation()
      const inputElement = inputRef.current

      const shouldShowCancel = inputElement?.value.length > 0
      if (!shouldShowCancel) {
        setShowCancelButton(false)
        setButtonSize(0)
      }
    }

    const handleCloseInput = () => {
      setDesktopVariantOpen(false)
    }

    const handleOpenInput = () => {
      if (disabled) return

      if (!desktopVariantOpen) {
        setDesktopVariantOpen(true)

        inputRef.current.focus()
      }
    }

    useEffect(() => {
      const handleEsc = (event) => {
        event.stopPropagation()
        event.key === 'Escape' && handleCloseInput()
      }
      const handleClick = (event) => {
        event.stopPropagation()
        if (containerRef.current || isOpenInput) {
          if (!containerRef.current.contains(event.target)) {
            if (defineValue.trim() === '' && !defaultDesktopVariantOpen) {
              handleCloseInput()
            }
          }
        }
      }

      document.addEventListener('keydown', handleEsc)
      document.addEventListener('mousedown', handleClick)

      return () => {
        document.removeEventListener('keydown', handleEsc)
        document.removeEventListener('mousedown', handleClick)
      }
    }, [containerRef, inputRef, isOpenInput])

    useEffect(() => {
      if (startFocued) {
        inputRef.current.focus()
      }
    }, [startFocued])

    useEffect(() => {
      const hasValue = !isEmpty(defineValue)

      if (hasValue !== showClearButton) {
        setShowClearButton(hasValue)
      }
    }, [defineValue])

    return (
      <SearchContext.Provider
        value={{
          variant,
          disabled,
          isOpenInput,
          inputRef,
          containerRef,
          buttonSize,
          showClearButton,
          showCancelButton,
          cancelButtonRef,
          value: defineValue,
          handleClear,
          handleCancel,
          handleInputChange,
          handleFocus,
          handleOnBlur,
          handleOpenInput,
          ...props
        }}
      >
        <Container>{children}</Container>
      </SearchContext.Provider>
    )
  }
)

SearchRoot.propTypes = {
  variant: PropTypes.oneOf(Object.values(searchVariantsOptions)),
  disabled: PropTypes.bool,
  defaultDesktopVariantOpen: PropTypes.bool,
  customOnChange: PropTypes.func,
  children: PropTypes.node
}

SearchRoot.displayName = 'SearchRoot'

export default SearchRoot
